const isDevelopment = process.env["NODE_ENV"] === "development";

const SENSITIVE_EXACT = new Set(["password", "secret", "authorization", "cookie"]);
const SENSITIVE_SUBSTRINGS = ["token", "secret", "password", "authorization"];

export function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (SENSITIVE_EXACT.has(lower)) return true;
  for (const s of SENSITIVE_SUBSTRINGS) {
    if (lower.includes(s)) return true;
  }
  if (lower === "key") return true;
  if (lower.endsWith("_key") || lower.endsWith("-key") || lower.endsWith(".key")) return true;
  if (lower.endsWith("key") && /[a-z]key$/i.test(key)) {
    if (lower === "monkey" || lower === "turkey" || lower === "donkey") return false;
    return true;
  }
  if (lower.includes("_key_") || lower.includes("-key-") || lower.includes(".key.")) return true;
  return false;
}

export function sanitize(arg: unknown, seen = new WeakSet<object>()): unknown {
  if (arg === null || arg === undefined) return arg;
  if (typeof arg === "string" || typeof arg === "number" || typeof arg === "boolean" || typeof arg === "bigint" || typeof arg === "symbol") {
    return arg;
  }
  if (arg instanceof Error) {
    const errObj: Record<string, unknown> = {
      name: arg.name,
      message: arg.message,
      stack: arg.stack,
    };
    for (const key of Object.keys(arg as unknown as Record<string, unknown>)) {
      if (key === "name" || key === "message" || key === "stack") continue;
      const val = (arg as unknown as Record<string, unknown>)[key];
      errObj[key] = isSensitiveKey(key) ? "***REDACTED***" : sanitize(val, seen);
    }
    if ((arg as unknown as { cause?: unknown }).cause !== undefined) {
      errObj["cause"] = sanitize((arg as unknown as { cause: unknown }).cause, seen);
    }
    if ((arg as unknown as { digest?: unknown }).digest !== undefined) {
      errObj["digest"] = sanitize((arg as unknown as { digest: unknown }).digest, seen);
    }
    return errObj;
  }
  if (typeof arg === "object") {
    if (seen.has(arg as object)) return "[Circular]";
    seen.add(arg as object);
    if (Array.isArray(arg)) {
      return (arg as unknown[]).map((v) => sanitize(v, seen));
    }
    if (arg instanceof Date || arg instanceof RegExp) return arg;
    if (arg instanceof Map) {
      const entries: unknown[] = [];
      for (const [k, v] of (arg as Map<unknown, unknown>).entries()) {
        const sk = String(k);
        entries.push([isSensitiveKey(sk) ? "***REDACTED***" : sk, sanitize(v, seen)]);
      }
      return entries;
    }
    if (arg instanceof Set) {
      return Array.from(arg as Set<unknown>).map((v) => sanitize(v, seen));
    }
    const obj = arg as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
      if (isSensitiveKey(key)) {
        sanitized[key] = "***REDACTED***";
      } else {
        sanitized[key] = sanitize(obj[key], seen);
      }
    }
    return sanitized;
  }
  return arg;
}

export const logger = {
  error: (...args: unknown[]) => {
    console.error(...args.map((a) => sanitize(a)));
  },
  warn: (...args: unknown[]) => {
    console.warn(...args.map((a) => sanitize(a)));
  },
  info: (...args: unknown[]) => {
    if (isDevelopment) console.info(...args.map((a) => sanitize(a)));
  },
  debug: (...args: unknown[]) => {
    if (isDevelopment) console.debug("[DEBUG]", ...args.map((a) => sanitize(a)));
  },
};
